import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { LoadingButton, Notice, PageHeader } from '@routes-plan/ui';
import { AdminPage, useAlert } from '../shared/AdminUi';
import PermissionButton from '../../../components/common/PermissionButton';
import StandardModal from '../../../components/modales/StandardModal';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { setPageTitle } from '../../../utils/pageTitle';
import { apiErrorMessage } from '../../inventory/shared/inventoryApi';
import { useT, type TextKey } from '../../../i18n';

/**
 * Administrar → Configuración de rutas. Valores generales de la empresa: cada ruta nueva sale con ellos
 * y ahí se pueden dejar o cambiar. Guardar aquí no toca las rutas existentes; «Aplicar a las rutas
 * existentes» copia solo las opciones que se marquen.
 */
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

export const ROUTE_DEFAULTS_MENU = '/admin/route-defaults';

type Kind = 'money' | 'int' | 'bool' | 'time' | 'optionalInt' | 'optionalMoney';
type Section = 'loans' | 'fee' | 'records' | 'app';
interface FieldDef {
  key: string;
  kind: Kind;
  section: Section;
  label: TextKey;
  help?: TextKey;
  /** Solo se muestra si esta opción está activa (las horas, con el horario). */
  dependsOn?: string;
}

export const ROUTE_DEFAULT_FIELDS: FieldDef[] = [
  { key: 'creditLimit', kind: 'money', section: 'loans', label: 'routes.creditLimit' },
  { key: 'discountDays', kind: 'int', section: 'loans', label: 'routes.discountDaysRule' },
  { key: 'blockNewLoanAfterInstallments', kind: 'optionalInt', section: 'loans', label: 'routes.blockLoanAfterInstallments', help: 'routes.blockLoanAfterInstallmentsHelp' },
  { key: 'allowClientCreditLimit', kind: 'bool', section: 'loans', label: 'routes.useClientCreditLimit' },
  { key: 'allowMultipleLoans', kind: 'bool', section: 'loans', label: 'routes.defaults.allowMultipleLoans', help: 'routeDevices.loansTooltip' },
  { key: 'allowRefinance', kind: 'bool', section: 'loans', label: 'routes.defaults.allowRefinance', help: 'routeDevices.refinanceTooltip' },
  { key: 'maxRefinancesPerDay', kind: 'optionalInt', section: 'loans', label: 'routes.maxRefinancesPerDay', help: 'routes.maxRefinancesPerDayHelp' },
  { key: 'insurance', kind: 'money', section: 'fee', label: 'routes.feePerBase', help: 'routes.feePerBaseHelp' },
  { key: 'insuranceRate', kind: 'money', section: 'fee', label: 'routes.calculationBase', help: 'routes.calculationBaseHelp' },
  { key: 'manualInsurance', kind: 'bool', section: 'fee', label: 'routes.manualFee', help: 'routeDevices.manualFeeTooltip' },
  { key: 'allowManualDiscount', kind: 'bool', section: 'records', label: 'routes.defaults.allowManualDiscount', help: 'routeDevices.manualDiscountTooltip' },
  { key: 'maxDiscountPerDay', kind: 'optionalMoney', section: 'records', label: 'routes.maxDiscountPerDay', help: 'routes.maxDiscountPerDayHelp' },
  { key: 'maxExpensesPerDay', kind: 'optionalMoney', section: 'records', label: 'routes.maxExpensesPerDay', help: 'routes.maxExpensesPerDayHelp' },
  { key: 'allowDeletePayment', kind: 'bool', section: 'records', label: 'routes.defaults.allowDeletePayment', help: 'routeDevices.deletePaymentsTooltip' },
  { key: 'allowDeleteLoan', kind: 'bool', section: 'records', label: 'routes.defaults.allowDeleteLoan', help: 'routeDevices.deleteLoansTooltip' },
  { key: 'requireExpensePhoto', kind: 'bool', section: 'records', label: 'routes.requireExpensePhoto', help: 'routes.requireExpensePhotoHelp' },
  { key: 'allowRepay', kind: 'bool', section: 'app', label: 'routes.defaults.allowRepay', help: 'routeDevices.settleTooltip' },
  { key: 'enforceSchedule', kind: 'bool', section: 'app', label: 'routes.defaults.enforceSchedule', help: 'routeDevices.scheduleTooltip' },
  { key: 'openingTime', kind: 'time', section: 'app', label: 'routeDevices.openTime', help: 'routeDevices.openTimeTooltip', dependsOn: 'enforceSchedule' },
  { key: 'closingTime', kind: 'time', section: 'app', label: 'routeDevices.closeTime', help: 'routeDevices.closeTimeTooltip', dependsOn: 'enforceSchedule' },
  { key: 'allowDeviceRegistration', kind: 'bool', section: 'app', label: 'routes.defaults.allowDeviceRegistration', help: 'routeDevices.registrationTooltip' },
  { key: 'deviceDetail', kind: 'bool', section: 'app', label: 'routes.defaults.deviceDetail', help: 'routeDevices.detailTooltip' },
];

const SECTIONS: { id: Section; title: TextKey }[] = [
  { id: 'loans', title: 'routes.defaults.sectionLoans' },
  { id: 'fee', title: 'routes.defaults.sectionFee' },
  { id: 'records', title: 'routes.defaults.sectionRecords' },
  { id: 'app', title: 'routes.defaults.sectionApp' },
];

type Values = Record<string, string | boolean>;

/** De la respuesta del API a los valores del formulario (los números como texto). */
const toForm = (settings: Record<string, unknown>): Values =>
  Object.fromEntries(
    ROUTE_DEFAULT_FIELDS.map((f) => {
      const v = settings?.[f.key];
      if (f.kind === 'bool') return [f.key, Boolean(v)];
      if (f.kind === 'optionalInt' || f.kind === 'optionalMoney') return [f.key, v ? String(v) : ''];
      return [f.key, v === null || v === undefined ? '' : String(v)];
    }),
  );

const toApi = (values: Values) =>
  Object.fromEntries(
    ROUTE_DEFAULT_FIELDS.map((f) => {
      const v = values[f.key];
      if (f.kind === 'bool') return [f.key, Boolean(v)];
      if (f.kind === 'optionalInt' || f.kind === 'optionalMoney') return [f.key, Number(v) > 0 ? Number(v) : null];
      if (f.kind === 'time') return [f.key, String(v || '')];
      return [f.key, Number(v) || 0];
    }),
  );

const RouteDefaults: React.FC<ShowAlerts> = () => {
  const t = useT();
  const showAlert = useAlert();
  const [saving, setSaving] = useState(false);
  setPageTitle('menu.admin.route-defaults');
  const { canUpdate } = useResourcePermissions(ROUTE_DEFAULTS_MENU);
  const [values, setValues] = useState<Values>({});
  const [saved, setSaved] = useState<Values>({});
  const [routeCount, setRouteCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyKeys, setApplyKeys] = useState<string[]>([]);
  const [applying, setApplying] = useState(false);

  const dirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(saved), [values, saved]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('route-defaults');
        const form = toForm(data.settings || {});
        setValues(form);
        setSaved(form);
        setRouteCount(Number(data.routeCount) || 0);
        setLoaded(true);
      } catch (error) {
        showAlert(t('common.loadError'), 'error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setValue = (key: string, v: string | boolean) => setValues((prev) => ({ ...prev, [key]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put('route-defaults', { settings: toApi(values) });
      const form = toForm(data.settings || {});
      setValues(form);
      setSaved(form);
      showAlert(t('routes.defaults.saved'), 'success');
    } catch (error) {
      showAlert(apiErrorMessage(error, t('common.saveError')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const openApply = () => {
    setApplyKeys(ROUTE_DEFAULT_FIELDS.map((f) => f.key));
    setApplyOpen(true);
  };

  const apply = async () => {
    if (!applyKeys.length) {
      showAlert(t('routes.defaults.applyNone'), 'warning');
      return;
    }
    setApplying(true);
    try {
      const { data } = await axios.post('route-defaults/apply', { keys: applyKeys });
      setApplyOpen(false);
      showAlert(t('routes.defaults.applyDone', { count: Number(data.routes) || 0 }), 'success');
    } catch (error) {
      showAlert(apiErrorMessage(error, t('common.saveError')), 'error');
    } finally {
      setApplying(false);
    }
  };

  const renderField = (f: FieldDef) => {
    if (f.dependsOn && !values[f.dependsOn]) return null;
    const disabled = !canUpdate;
    if (f.kind === 'bool') {
      return (
        <Box key={f.key} sx={{ minWidth: 0 }}>
          <FormControlLabel
            control={<Switch checked={Boolean(values[f.key])} onChange={(e) => setValue(f.key, e.target.checked)} disabled={disabled} />}
            label={t(f.label)}
            sx={{ alignItems: 'flex-start', m: 0, '& .MuiSwitch-root': { mt: -0.75 }, '& .MuiFormControlLabel-label': { overflowWrap: 'anywhere' } }}
            data-tour={`route-defaults-${f.key}`}
          />
          {f.help && <FormHelperText sx={{ ml: 7, mt: 0 }}>{t(f.help)}</FormHelperText>}
        </Box>
      );
    }
    return (
      <TextField
        key={f.key}
        fullWidth
        size="small"
        type={f.kind === 'time' ? 'time' : 'number'}
        label={t(f.label)}
        value={values[f.key] ?? ''}
        placeholder={f.kind === 'optionalMoney' || f.key === 'maxRefinancesPerDay' ? t('routes.noLimit') : f.kind === 'optionalInt' ? t('routes.blockLoanAfterInstallmentsOff') : '0'}
        onChange={(e) => {
          const raw = e.target.value;
          setValue(f.key, f.kind === 'optionalInt' || f.kind === 'int' ? raw.replace(/[^0-9]/g, '').slice(0, 4) : raw);
        }}
        inputProps={f.kind === 'time' ? { step: 900 } : { min: 0, inputMode: f.kind === 'money' || f.kind === 'optionalMoney' ? 'decimal' : 'numeric' }}
        InputLabelProps={f.kind === 'time' ? { shrink: true } : undefined}
        helperText={f.help ? t(f.help) : undefined}
        disabled={disabled}
        data-tour={`route-defaults-${f.key}`}
      />
    );
  };

  return (
    <AdminPage>
      <PageHeader
        title={t('routes.defaults.title')}
        description={t('routes.defaults.subtitle')}
      />
      <Box data-tour="route-defaults-intro">
        <Notice severity="info">{t('routes.defaults.intro')}</Notice>
      </Box>
      {!canUpdate && loaded && <Notice severity="info">{t('routes.defaults.readOnly')}</Notice>}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
          alignItems: 'start',
        }}
      >
        {SECTIONS.map((s) => (
          <Paper key={s.id} component="section" variant="outlined" sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
              {t(s.title)}
            </Typography>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'minmax(0, 1fr)' }}>
              {ROUTE_DEFAULT_FIELDS.filter((f) => f.section === s.id).map(renderField)}
            </Box>
          </Paper>
        ))}
      </Box>
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          py: 1.5,
          bgcolor: 'background.default',
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {dirty && (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', flexBasis: { xs: '100%', sm: 'auto' } }}>
            {t('routes.defaults.applySaveFirst')}
          </Typography>
        )}
        <PermissionButton
          variant="outlined"
          color="inherit"
          onClick={openApply}
          allowed={canUpdate}
          disabled={!loaded || dirty || routeCount === 0}
          deniedTooltip={t('common.noPermissionEdit')}
          data-tour="route-defaults-apply"
        >
          {t('routes.defaults.applyButton')}
        </PermissionButton>
        {canUpdate && (
          <LoadingButton variant="contained" onClick={save} loading={saving} disabled={!loaded || !dirty} data-tour="route-defaults-save">
            {t('routes.defaults.save')}
          </LoadingButton>
        )}
      </Box>
      <StandardModal
        open={applyOpen}
        onClose={() => !applying && setApplyOpen(false)}
        title={t('routes.defaults.applyTitle')}
        subtitle={t('routes.defaults.applyIntro', { count: routeCount })}
        maxWidth={640}
        disableClose={applying}
        actions={
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: 1 }}>
            <Button variant="outlined" onClick={() => setApplyOpen(false)} disabled={applying} color="inherit">
              {t('common.cancel')}
            </Button>
            <LoadingButton variant="contained" color="warning" onClick={apply} loading={applying} disabled={!applyKeys.length}>
              {t('routes.defaults.applyConfirm', { count: routeCount })}
            </LoadingButton>
          </Stack>
        }
      >
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button size="small" onClick={() => setApplyKeys(ROUTE_DEFAULT_FIELDS.map((f) => f.key))}>
            {t('routes.defaults.selectAll')}
          </Button>
          <Button size="small" onClick={() => setApplyKeys([])}>
            {t('routes.defaults.selectNone')}
          </Button>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, columnGap: 2 }}>
          {ROUTE_DEFAULT_FIELDS.map((f) => (
            <FormControlLabel
              key={f.key}
              control={
                <Checkbox
                  size="small"
                  checked={applyKeys.includes(f.key)}
                  onChange={(e) =>
                    setApplyKeys((prev) => (e.target.checked ? [...prev, f.key] : prev.filter((k) => k !== f.key)))
                  }
                />
              }
              label={<Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{t(f.label)}</Typography>}
              sx={{ minWidth: 0, mr: 0 }}
            />
          ))}
        </Box>
        <Box sx={{ mt: 2 }}>
          <Notice severity="warning">{t('routes.defaults.applyWarning')}</Notice>
        </Box>
      </StandardModal>
    </AdminPage>
  );
};

export default RouteDefaults;
